import { createFileRoute, Link } from "@tanstack/react-router";
import { AppShell } from "@/components/app-shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Briefcase, Users, MapPin, Plus } from "lucide-react";

export const Route = createFileRoute("/projects/")({
  head: () => {
    const title = "Community Projects — Xamseen";
    const description =
      "Discover volunteer-led Xamseen projects: neighbourhood initiatives turning local reports into real improvements people can join.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: "https://xamseen.com/projects" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: "https://xamseen.com/projects" }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Community Projects on Xamseen",
            description,
            url: "https://xamseen.com/projects",
          }),
        },
      ],
    };
  },
  component: ProjectsPage,
});


function ProjectsPage() {
  const [projects, setProjects] = useState<any[]>([]);
  useEffect(() => {
    supabase.from("projects").select("*").order("created_at", { ascending: false }).limit(50).then(({ data }) => setProjects(data ?? []));
  }, []);

  return (
    <AppShell>
      <div className="mx-auto max-w-5xl space-y-6">
        <header className="flex items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight">Community Projects</h1>
            <p className="text-muted-foreground">Volunteer-led initiatives improving neighborhoods.</p>
          </div>
          <Button><Plus className="mr-2 h-4 w-4" />New project</Button>
        </header>

        {projects.length === 0 ? (
          <Card className="p-12 text-center">
            <Briefcase className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
            <p className="font-medium">No projects yet</p>
            <p className="text-sm text-muted-foreground">Be the first to start a community initiative.</p>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {projects.map((p) => (
              <Card key={p.id} className="overflow-hidden">
                {p.cover_url && <img src={p.cover_url} alt="" className="h-40 w-full object-cover" />}
                <div className="space-y-3 p-5">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="capitalize">{p.status}</Badge>
                    <Badge variant="outline" className="capitalize">{p.category}</Badge>
                  </div>
                  <h3 className="font-display text-lg font-semibold">{p.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">{p.description}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    {p.location_name && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{p.location_name}</span>}
                    <span className="flex items-center gap-1"><Users className="h-3 w-3" />{p.volunteer_count}/{p.volunteer_target || "∞"} volunteers</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
